import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';

const FILTERS = [
  { key: '', label: 'All' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'today', label: 'Due today' },
  { key: 'upcoming', label: 'Upcoming' },
];

const STATUS_STYLES = {
  overdue: 'bg-red-100 text-red-700',
  today: 'bg-orange-100 text-orange-700',
  upcoming: 'bg-blue-50 text-[#2563EB]',
  done: 'bg-green-100 text-green-700',
};

const STATUS_LABELS = {
  overdue: 'Overdue',
  today: 'Due today',
  upcoming: 'Upcoming',
  done: 'Done',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function BusinessFollowUpsPage() {
  const [filter, setFilter] = useState('');
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [busyId, setBusyId] = useState(null);

  const fetchFollowUps = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const { data } = await api.get('/admin/follow-ups', {
        params: { filter: filter || undefined },
      });
      setFollowUps(data.data || []);
    } catch (err) {
      console.error('Failed to load follow-ups', err);
      setErrorMsg('Failed to load follow-ups.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchFollowUps();
  }, [fetchFollowUps]);

  const handleComplete = async (id) => {
    setBusyId(id);
    const previous = followUps;
    setFollowUps((prev) => prev.filter((f) => f.id !== id));
    try {
      await api.patch(`/admin/follow-ups/${id}/complete`);
    } catch (err) {
      console.error('Failed to complete follow-up', err);
      setFollowUps(previous);
      setErrorMsg('Failed to mark as done. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this follow-up?')) return;
    setBusyId(id);
    const previous = followUps;
    setFollowUps((prev) => prev.filter((f) => f.id !== id));
    try {
      await api.delete(`/admin/follow-ups/${id}`);
    } catch (err) {
      console.error('Failed to delete follow-up', err);
      setFollowUps(previous);
      setErrorMsg('Failed to delete. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#0D1B3D]">Follow-ups</h2>
      </div>

      {errorMsg && (
        <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          {errorMsg}
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
              filter === f.key ? 'bg-[#2563EB] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {loading && <div className="text-sm text-gray-400 py-8 text-center">Loading…</div>}
        {!loading && followUps.length === 0 && (
          <div className="text-sm text-gray-400 py-8 text-center">No follow-ups here. Nice and clear!</div>
        )}
        {!loading && followUps.map((f) => (
          <div key={f.id} className="bg-white rounded-xl shadow p-4 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-[#0D1B3D]">{f.business?.business_name || 'Unknown business'}</span>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${STATUS_STYLES[f.status] || STATUS_STYLES.upcoming}`}>
                  {STATUS_LABELS[f.status] || 'Follow-up'}: {formatDate(f.due_at)}
                </span>
              </div>
              <div className="text-sm text-gray-700 mt-1">{f.note}</div>
              <div className="text-xs text-gray-400 mt-1 flex flex-wrap gap-x-3">
                {f.business?.phone && <span>{f.business.phone}</span>}
                {f.business?.email && <span>{f.business.email}</span>}
                {f.creator?.name && <span>Added by {f.creator.name}</span>}
              </div>
            </div>

            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <button
                type="button"
                onClick={() => handleComplete(f.id)}
                disabled={busyId === f.id}
                className="text-xs font-medium text-green-600 hover:underline whitespace-nowrap disabled:opacity-50"
              >
                Mark done
              </button>
              <button
                type="button"
                onClick={() => handleDelete(f.id)}
                disabled={busyId === f.id}
                className="text-xs font-medium text-red-600 hover:underline whitespace-nowrap disabled:opacity-50"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}